<template>
  <span
    class="user-avatar-badge"
    :class="['size-' + size]"
    :style="badgeStyle"
    :title="tooltip"
  >
    <img v-if="src" :src="src" class="avatar-img" alt="" @error="onImgError" />
    <span v-else class="avatar-initials">{{ initials }}</span>
  </span>
</template>

<script>
import { getBaseURL } from "../api/http";

const PALETTE = [
  "#3a7bd5", "#00b09b", "#e67e22", "#8e44ad",
  "#16a085", "#c0392b", "#2980b9", "#d35400",
  "#27ae60", "#7f8c8d", "#2c3e50", "#e74c3c",
];

export default {
  name: "UserAvatar",
  props: {
    name: { type: String, default: "" },
    username: { type: String, default: "" },
    avatar: { type: String, default: "" },
    userId: { type: [Number, String], default: null },
    size: { type: String, default: "md" }, // sm | md | lg | xl
  },
  data() {
    return { imgFailed: false };
  },
  computed: {
    src() {
      if (this.imgFailed || !this.avatar) return "";
      const path = String(this.avatar).trim();
      if (!path) return "";
      if (/^https?:\/\//i.test(path) || path.startsWith("blob:")) {
        // 兼容旧直链：/api/upload/avatars/xxx 被 Nginx 静态规则拦截，改走 view 接口
        const m = path.match(/\/upload\/avatars\/([^/?#]+)$/i);
        if (m) {
          return getBaseURL().replace(/\/$/, "") + "/yj/avatar/view?f=" + encodeURIComponent(m[1]);
        }
        return path;
      }
      const base = getBaseURL().replace(/\/$/, "");
      const m = path.replace(/\\/g, "/").match(/(?:^|\/)upload\/avatars\/([^/?#]+)$/i);
      if (m) {
        return base + "/yj/avatar/view?f=" + encodeURIComponent(m[1]);
      }
      return path.startsWith("/") ? base + path : base + "/" + path;
    },
    initials() {
      const n = (this.name || this.username || "?").trim();
      if (!n) return "?";
      // 中文取末两字，英文取前两字母
      if (/[\u4e00-\u9fff]/.test(n)) {
        return n.length >= 2 ? n.slice(-2) : n;
      }
      return n.slice(0, 2).toUpperCase();
    },
    colorSeed() {
      const key = String(this.userId || this.username || this.name || "0");
      let h = 0;
      for (let i = 0; i < key.length; i++) {
        h = (h * 31 + key.charCodeAt(i)) >>> 0;
      }
      return PALETTE[h % PALETTE.length];
    },
    badgeStyle() {
      if (this.src) return {};
      return { backgroundColor: this.colorSeed };
    },
    tooltip() {
      const parts = [this.name, this.username].filter(Boolean);
      return parts.join(" · ");
    },
  },
  watch: {
    avatar() {
      this.imgFailed = false;
    },
  },
  methods: {
    onImgError() {
      this.imgFailed = true;
    },
  },
};
</script>

<style scoped>
.user-avatar-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  color: #fff;
  font-weight: 600;
  line-height: 1;
  vertical-align: middle;
  background: #909399;
}
.size-sm {
  width: 22px;
  height: 22px;
  font-size: 10px;
}
.size-md {
  width: 28px;
  height: 28px;
  font-size: 11px;
}
.size-lg {
  width: 40px;
  height: 40px;
  font-size: 13px;
}
.size-xl {
  width: 96px;
  height: 96px;
  font-size: 28px;
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.avatar-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-align: center;
  line-height: 1;
  user-select: none;
  /* 避免字间距把中文挤偏 */
  letter-spacing: 0;
}
</style>
